"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { c as _c } from "react/compiler-runtime";
import { ResponsiveContainer, Tooltip, Treemap } from "recharts";
import React from "react";
import { cx } from "@/lib/tailwind-utils";
const PALETTE: string[] = ["#3b82f6",
// blue
"#10b981",
// emerald
"#8b5cf6",
// violet
"#f59e0b",
// amber
"#06b6d4",
// cyan
"#ec4899",
// pink
"#84cc16",
// lime
"#d946ef",
// fuchsia
"#6b7280" // gray
];
interface TreemapCellProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  value?: number;
  depth?: number;
  index?: number;
}
const TreemapCell = t0 => {
  const $ = _c(17);
  const {
    x: t1,
    y: t2,
    width: t3,
    height: t4,
    name: t5,
    value: t6,
    index: t7
  } = t0;
  const x = t1 === undefined ? 0 : t1;
  const y = t2 === undefined ? 0 : t2;
  const width = t3 === undefined ? 0 : t3;
  const height = t4 === undefined ? 0 : t4;
  const name = t5 === undefined ? "" : t5;
  const value = t6 === undefined ? 0 : t6;
  const index = t7 === undefined ? 0 : t7;
  const color = PALETTE[index % PALETTE.length];
  const showLabel = width >= 60 && height >= 30;
  let t8;
  if ($[0] !== color || $[1] !== height || $[2] !== width || $[3] !== x || $[4] !== y) {
    t8 = <rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.85} stroke="var(--background)" strokeWidth={2} rx={3} />;
    $[0] = color;
    $[1] = height;
    $[2] = width;
    $[3] = x;
    $[4] = y;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== height || $[7] !== name || $[8] !== showLabel || $[9] !== value || $[10] !== width || $[11] !== x || $[12] !== y) {
    t9 = showLabel && <><text x={x + width / 2} y={y + height / 2 - 6} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize={12} fontWeight={500} style={{
        pointerEvents: "none"
      }}>{name}</text><text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.85)" fontSize={11} style={{
        pointerEvents: "none"
      }}>{value.toLocaleString()}</text></>;
    $[6] = height;
    $[7] = name;
    $[8] = showLabel;
    $[9] = value;
    $[10] = width;
    $[11] = x;
    $[12] = y;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <g>{t8}{t9}</g>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
};
interface TreemapTooltipProps {
  active?: boolean;
  payload?: any[];
  valueFormatter?: (v: number) => string;
}
const TreemapTooltip = ({
  active,
  payload,
  valueFormatter = v => v.toLocaleString()
}: TreemapTooltipProps) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  const name: string = item.name ?? "";
  const size: number = item.size ?? item.value ?? 0;
  return <div className="rounded-md border border-border bg-background/90 px-3 py-2 text-body shadow-md backdrop-blur-2xl">
      <p className="text-foreground">
        <span className="font-medium">{name}</span>
        <span className="ml-2 tabular-nums text-foreground-secondary">
          {valueFormatter(size)}
        </span>
      </p>
    </div>;
};
export interface TreemapChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[];
  index: string;
  category: string;
  valueFormatter?: (v: number) => string;
}
const TreemapChart = React.forwardRef<HTMLDivElement, TreemapChartProps>((t0, forwardedRef) => {
  const $ = _c(26);
  let category;
  let className;
  let data;
  let index;
  let other;
  let valueFormatter;
  if ($[0] !== t0) {
    ({
      data,
      index,
      category,
      valueFormatter,
      className,
      ...other
    } = t0);
    $[0] = t0;
    $[1] = category;
    $[2] = className;
    $[3] = data;
    $[4] = index;
    $[5] = other;
    $[6] = valueFormatter;
  } else {
    category = $[1];
    className = $[2];
    data = $[3];
    index = $[4];
    other = $[5];
    valueFormatter = $[6];
  }
  let t1;
  if ($[7] !== category || $[8] !== data || $[9] !== index) {
    let t2;
    if ($[11] !== category || $[12] !== index) {
      t2 = row => ({
        name: String(row[index] ?? ""),
        size: typeof row[category] === "number" ? row[category] : Number(row[category]) || 0
      });
      $[11] = category;
      $[12] = index;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    t1 = data.map(t2);
    $[7] = category;
    $[8] = data;
    $[9] = index;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const treemapData = t1;
  let t2;
  if ($[14] !== className) {
    t2 = cx("h-80 w-full", className);
    $[14] = className;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== valueFormatter) {
    t3 = <Tooltip content={t4 => {
      const {
        active,
        payload
      } = t4;
      return <TreemapTooltip active={active} payload={payload ? [...payload] : []} valueFormatter={valueFormatter} />;
    }} />;
    $[16] = valueFormatter;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== t3 || $[19] !== treemapData) {
    t4 = <ResponsiveContainer width="100%" height="100%"><Treemap data={treemapData} dataKey="size" content={TreemapCell as any}>{t3}</Treemap></ResponsiveContainer>;
    $[18] = t3;
    $[19] = treemapData;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== forwardedRef || $[22] !== other || $[23] !== t2 || $[24] !== t4) {
    t5 = <div ref={forwardedRef} className={t2} tremor-id="tremor-raw" {...other}>{t4}</div>;
    $[21] = forwardedRef;
    $[22] = other;
    $[23] = t2;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  return t5;
});
TreemapChart.displayName = "TreemapChart";
export { TreemapChart };